import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Toaster } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Bot, Calendar, Bug, Github, Kanban, PanelLeft, PanelLeftClose, Moon, Sun, MoreVertical } from 'lucide-react';
import packageJson from '../../../package.json';
import { useTheme } from '../../context/ThemeContext';
import { reportUiError } from '../../sentry';
import { githubRepoUrl, LCCTO_REPOS } from '../../lib/lcctoRepos';
import AppSidebar, {
  readSidebarOpenPreference,
  persistSidebarOpenPreference,
} from './AppSidebar';
import TasksSidebar, {
  persistTasksSidebarOpenPreference,
  readTasksSidebarOpenPreference,
} from './TasksSidebar';
import ConnectionHeaderSearch from './ConnectionHeaderSearch';
import DashboardTrendRangeSelect from '../Engine/DashboardTrendRangeSelect';
import { DashboardChartProvider } from '../../context/DashboardChartHoverContext';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => readSidebarOpenPreference());
  const [isTasksSidebarOpen, setIsTasksSidebarOpen] = useState(() => readTasksSidebarOpenPreference());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isDark = theme === 'dark';

  /**
   * External companion apps surfaced from the header. These open in a new tab
   * — they are not internal routes — so we don't gate them through `navigate`.
   */
  const externalTools: Array<{
    label: string;
    href: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    /** When true (default) the link opens in a new tab. Internal app routes set this to false. */
    external?: boolean;
  }> = [
    {
      label: 'Stephan OS GPT',
      href: 'https://chatgpt.com/g/g-694da54692508191a44d0215f96479c7-stephan-os',
      description: 'Open the custom ChatGPT trained on your context to brainstorm and draft.',
      icon: Bot,
      color: 'text-emerald-500',
      external: true,
    },
    {
      label: 'Channels debug',
      href: '/debug/channels',
      description: 'Live JSON dump of channel catalog + discovered slugs and per-channel recipient counts.',
      icon: Bug,
      color: 'text-rose-500',
      external: false,
    },
  ];

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      reportUiError('auth', 'sign_out', error);
      console.error('Error signing out:', error);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      persistSidebarOpenPreference(next);
      return next;
    });
  };

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      if (!prev) return prev;
      persistSidebarOpenPreference(false);
      return false;
    });
  }, []);

  const openSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      if (prev) return prev;
      persistSidebarOpenPreference(true);
      return true;
    });
  }, []);

  const toggleTasksSidebar = () => {
    setIsTasksSidebarOpen((prev) => {
      const next = !prev;
      persistTasksSidebarOpenPreference(next);
      return next;
    });
  };

  const closeTasksSidebar = useCallback(() => {
    setIsTasksSidebarOpen((prev) => {
      if (!prev) return prev;
      persistTasksSidebarOpenPreference(false);
      return false;
    });
  }, []);

  const openTasksSidebar = useCallback(() => {
    setIsTasksSidebarOpen((prev) => {
      if (prev) return prev;
      persistTasksSidebarOpenPreference(true);
      return true;
    });
  }, []);

  useEffect(() => {
    if (!isMoreOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMoreOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isMoreOpen]);

  const closeMoreMenu = () => setIsMoreOpen(false);
  const isDashboard = location.pathname === '/';
  const isTaskBoard = location.pathname === '/tasks/board';
  const isArcCanvas = /^\/narrative-arcs\/[^/]+/.test(location.pathname);

  const menuItemClass =
    'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left rounded-md transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50';

  const layout = (
    <>
      <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="w-full pl-3 pr-4 sm:pl-4 sm:pr-6 lg:pl-5 lg:pr-8">
          <div className="flex justify-between items-center h-16 gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(true)}
                className="lg:hidden shrink-0 p-1.5 -ml-0.5 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Open navigation"
              >
                <PanelLeft className="w-5 h-5" />
              </button>

              <button
                type="button"
                onClick={toggleSidebar}
                className="hidden lg:flex shrink-0 p-1.5 -ml-0.5 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                title={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {isSidebarOpen ? (
                  <PanelLeftClose className="w-5 h-5" />
                ) : (
                  <PanelLeft className="w-5 h-5" />
                )}
              </button>

              <button
                onClick={() => navigate('/')}
                className="shrink-0 text-xl font-semibold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors whitespace-nowrap"
              >
                Stephan OS
              </button>

              <ConnectionHeaderSearch className="hidden lg:block ml-1 sm:ml-2" />
            </div>
            
            <div className="flex items-center gap-2 shrink-0">
              {isDashboard ? <DashboardTrendRangeSelect /> : null}

              <button
                type="button"
                onClick={() => navigate('/tasks/board')}
                className={`flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                  isTaskBoard
                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title="Task board"
                aria-label="Task board"
              >
                <Kanban className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => navigate('/content-calendar')}
                className={`flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                  location.pathname === '/content-calendar'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title="General Calendar"
                aria-label="General Calendar"
              >
                <Calendar className="w-4 h-4" />
              </button>

              <div className="relative" ref={moreRef}>
                <button
                  type="button"
                  onClick={() => setIsMoreOpen((v) => !v)}
                  className={`flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                    isMoreOpen
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title="More options"
                  aria-haspopup="menu"
                  aria-expanded={isMoreOpen}
                  aria-label="More options"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>

                {isMoreOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-50 max-h-[min(32rem,calc(100vh-6rem))] overflow-y-auto"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        toggleTheme();
                        closeMoreMenu();
                      }}
                      className={menuItemClass}
                    >
                      {isDark ? <Sun className="w-4 h-4 shrink-0" /> : <Moon className="w-4 h-4 shrink-0" />}
                      {isDark ? 'Light mode' : 'Dark mode'}
                    </button>

                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />

                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      Tools
                    </div>
                    {externalTools.map((tool) => {
                      const Icon = tool.icon;
                      const isExternal = tool.external !== false;
                      const row = (
                        <>
                          <Icon className={`w-4 h-4 shrink-0 ${tool.color}`} />
                          <span className="truncate">{tool.label}</span>
                        </>
                      );

                      return isExternal ? (
                        <a
                          key={tool.href}
                          role="menuitem"
                          href={tool.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMoreMenu}
                          className={menuItemClass}
                        >
                          {row}
                        </a>
                      ) : (
                        <Link
                          key={tool.href}
                          role="menuitem"
                          to={tool.href}
                          onClick={closeMoreMenu}
                          className={menuItemClass}
                        >
                          {row}
                        </Link>
                      );
                    })}

                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />

                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      GitHub
                    </div>
                    {LCCTO_REPOS.map((r) => {
                      const href = githubRepoUrl(r.owner, r.repo);
                      return (
                        <a
                          key={r.id}
                          role="menuitem"
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMoreMenu}
                          className={menuItemClass}
                        >
                          <Github className="w-4 h-4 shrink-0 text-gray-700 dark:text-gray-200" />
                          <span className="min-w-0 truncate">{r.label}</span>
                        </a>
                      );
                    })}

                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        closeMoreMenu();
                        void handleLogout();
                      }}
                      className={menuItemClass}
                    >
                      <LogOut className="w-4 h-4 shrink-0" />
                      Sign out
                    </button>

                    <div className="mt-1 pt-2 px-3 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-[11px] text-center text-gray-400 dark:text-gray-500 tabular-nums">
                        v{packageJson.version}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-4rem)]">
        <AppSidebar
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          onExpand={openSidebar}
          onAutoClose={closeSidebar}
          isMobileOpen={isMobileSidebarOpen}
          onMobileClose={() => setIsMobileSidebarOpen(false)}
        />

        <div className="flex-1 min-w-0 overflow-x-hidden">
          <div
            className={`max-w-7xl mx-auto px-3 sm:px-4 lg:px-5 xl:max-w-none xl:mx-0 xl:px-5 min-w-0 ${
              isArcCanvas ? 'py-2.5' : 'py-5'
            }`}
          >
            <main className="min-w-0">{children}</main>
          </div>
        </div>

        <TasksSidebar
          isOpen={isTasksSidebarOpen}
          onToggle={toggleTasksSidebar}
          onExpand={openTasksSidebar}
          onAutoClose={closeTasksSidebar}
        />
      </div>

      <Toaster position="bottom-left" richColors closeButton />
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {isDashboard ? <DashboardChartProvider>{layout}</DashboardChartProvider> : layout}
    </div>
  );
};

export default MainLayout;